import type {FC} from 'react';
import {memo} from 'react';
import {NavLink, useLocation} from 'react-router';

import classNames from 'classnames';
import {Observer} from 'mobx-react';

import {useI18n} from '@yourcoach/i18n';
import {currentUserStore} from '@yourcoach/shared/api/user';
import ChatsIconFilled from '@yourcoach/shared/assets/icons/primary/Chats.svg';
import CommunityIconFilled from '@yourcoach/shared/assets/icons/primary/Community 2.svg';
import ContentIconFilled from '@yourcoach/shared/assets/icons/primary/Content.svg';
import GoalsIconFilled from '@yourcoach/shared/assets/icons/primary/Goals.svg';
import HomeIconFilled from '@yourcoach/shared/assets/icons/primary/Home.svg';
import WidgetIconFilled from '@yourcoach/shared/assets/icons/primary/Password.svg';
import TutorialsIconFilled from '@yourcoach/shared/assets/icons/primary/Play.svg';
import ToDoIconFilled from '@yourcoach/shared/assets/icons/primary/To-Dos.svg';
import ChatsIcon from '@yourcoach/shared/assets/icons/secondary/Chats.svg';
import CommunityIcon from '@yourcoach/shared/assets/icons/secondary/Community 2.svg';
import ContentIcon from '@yourcoach/shared/assets/icons/secondary/Content.svg';
import GoalsIcon from '@yourcoach/shared/assets/icons/secondary/Goals.svg';
import HomeIcon from '@yourcoach/shared/assets/icons/secondary/Home.svg';
import WidgetIcon from '@yourcoach/shared/assets/icons/secondary/Password.svg';
import TutorialsIcon from '@yourcoach/shared/assets/icons/secondary/Play.svg';
import ToDoIcon from '@yourcoach/shared/assets/icons/secondary/Todos.svg';
import SupportIcon from '@yourcoach/shared/assets/icons/support.svg';
import UserGroupIcon from '@yourcoach/shared/assets/icons/user-group.svg';
import {CurrentUserService} from '@yourcoach/shared/core/system/currentUser/CurrentUser.service';
import {useInjection} from '@yourcoach/shared/IoC';
import {isIamCoach} from '@yourcoach/shared/utils/entity/isIamCoach';
import {
  isUserEmployee,
  isUserHasRole,
} from '@yourcoach/shared/utils/entity/user';
import {envManager} from '@yourcoach/shared/utils/env';
import {logger} from '@yourcoach/shared/utils/logger';
import {TestID} from '@yourcoach/shared/utils/test-utils/data-test-id';

import {
  labelChats,
  labelCoaches,
  labelContent,
  labelDashboard,
  labelGoals,
  labelMyCoaches,
  labelToDos,
  labelTutorials,
} from '@src/common/i18n/i18nMainMenu';
import localAppStore from '@src/context/appStore';
import {routerPath} from '@src/routes/path';
import {MainMenuBadge} from '@src/v2/components/MainMenuBadge';
import {MainMenuItem} from '@src/v2/components/MainMenuItem';
import {b2cDemoStore} from '@src/v2/modules/b2c-demo';

type MenuItem = {
  badge?: number;
  className: string;
  icon: FC;
  iconActive?: FC;
  id: string;
  isForCoach: boolean;
  label: string;
  path: string;
  testID?: string;
};

type Props = {
  menuOpened: boolean;
};

const MainMenu: FC<Props> = ({menuOpened}) => {
  const {t} = useI18n();
  const location = useLocation();
  const currentUserService = useInjection(CurrentUserService);

  const generateMenuItem = (): MenuItem[] => {
    const isUserCoach = currentUserService.hasRole('coach');
    const isUserCrmSpecialist = currentUserService.hasRole('crm_specialist');

    const items: MenuItem[] = [];

    items.push({
      className: 'Dashboard',
      icon: HomeIcon,
      iconActive: HomeIconFilled,
      id: 'your_space',
      isForCoach: isUserCoach,
      label: labelDashboard(),
      path: routerPath.yourSpace.$buildPath({}),
    });

    if (envManager.isCore) {
      if (isUserCrmSpecialist) {
        items.push({
          className: 'Crm',
          icon: UserGroupIcon,
          iconActive: UserGroupIcon,
          id: 'crm',
          isForCoach: isUserCoach,
          label: 'CRM',
          path: routerPath.crm.$buildPath({}),
        });
      }

      if (isUserCoach) {
        items.push({
          className: 'Coaches',
          icon: CommunityIcon,
          iconActive: CommunityIconFilled,
          id: 'community',
          isForCoach: isUserCoach,
          label: labelCoaches(),
          path: routerPath.community.$buildPath({}),
          testID: TestID.main_menu.button.community,
        });
      } else {
        const joined = currentUserStore.user?.counters.following.joined || 0;
        const matched = currentUserStore.user?.counters.following.matched || 0;
        const total = joined + matched;

        if (total > 0) {
          items.push({
            className: 'Coaches',
            icon: CommunityIcon,
            iconActive: CommunityIconFilled,
            id: 'my_coaches',
            isForCoach: isUserCoach,
            label: labelMyCoaches(total || 2),
            path: routerPath.myCoaches.$buildPath({}),
            testID: TestID.main_menu.button.my_coaches,
          });
        }
      }
    }

    items.push({
      className: 'To-dos',
      icon: ToDoIcon,
      iconActive: ToDoIconFilled,
      id: 'to_dos',
      isForCoach: isUserCoach,
      label: labelToDos(),
      path: routerPath.todos.$buildPath({
        searchParams: {
          t: isIamCoach() ? 'conference' : undefined,
        },
      }),
      testID: TestID.main_menu.button.to_dos,
    });

    items.push({
      className: 'Goals',
      icon: GoalsIcon,
      iconActive: GoalsIconFilled,
      id: 'goals',
      isForCoach: isUserCoach,
      label: labelGoals(),
      path: routerPath.goals.$buildPath({}),
      testID: TestID.main_menu.button.goals,
    });

    items.push({
      badge: localAppStore.postCreatedNotification,
      className: 'Chats',
      icon: ChatsIcon,
      iconActive: ChatsIconFilled,
      id: 'chats',
      isForCoach: isUserCoach,
      label: labelChats(),
      path: routerPath.chats.$buildPath({}),
      testID: TestID.main_menu.button.chats,
    });

    if (currentUserService.user?.hasArticlesAccess) {
      items.push({
        className: 'Content',
        icon: ContentIcon,
        iconActive: ContentIconFilled,
        id: 'content',
        isForCoach: isUserCoach,
        label: labelContent(),
        path: routerPath.content.$buildPath({}),
        testID: TestID.main_menu.button.content,
      });
    }

    if (b2cDemoStore.isActive.isTrue && !isUserCoach) {
      items.push({
        className: 'Widget',
        icon: WidgetIcon,
        iconActive: WidgetIconFilled,
        id: 'widget',
        isForCoach: isUserCoach,
        label: t('web-app:MainMenu.Widget'),
        path: routerPath.widget.$buildPath({
          searchParams: {
            tid: 'internal_test',
          },
        }),
        testID: TestID.main_menu.button.widget,
      });
    }

    if (isUserHasRole('support')) {
      items.push({
        badge: localAppStore.supportPostCreatedNotification,
        className: 'Support',
        icon: SupportIcon,
        id: 'support',
        isForCoach: isUserCoach,
        label: t('web-app:MainMenu.Support'),
        path: routerPath.supportChats.$buildPath({}),
      });
    }

    if (envManager.isCore && !isUserEmployee()) {
      items.push({
        className: 'Tutorials',
        icon: TutorialsIcon,
        iconActive: TutorialsIconFilled,
        id: 'tutorials',
        isForCoach: isUserCoach,
        label: labelTutorials(),
        path: routerPath.tutorials.$buildPath({}),
        testID: TestID.main_menu.button.tutorials,
      });
    }

    if (process.env.APP_ENV === 'dev') {
      items.push({
        className: 'New chats',
        icon: ChatsIcon,
        id: 'new_chats',
        isForCoach: isUserCoach,
        label: '[NEW] Chats',
        path: routerPath.chats.new.$buildPath({}),
      });
    }

    return items;
  };

  return (
    <Observer>
      {() => (
        <ul className="navbarNav">
          {generateMenuItem().map(item => {
            const isActive = location.pathname.includes(item.path);
            const icon = isActive ? (item.iconActive ?? item.icon) : item.icon;

            return (
              <li key={item.id}>
                <NavLink
                  className={({isActive}) =>
                    classNames(item.className, {selected: isActive})
                  }
                  data-test-id={item.testID}
                  onClick={() => logger.event(`side_menu_${item.id}_tap`)}
                  to={item.path}
                >
                  <MainMenuItem
                    icon={icon}
                    isActive={isActive}
                    isCoach={item.isForCoach}
                    label={item.label}
                    showBadge={Boolean(!menuOpened && item.badge)}
                  >
                    {item.badge ? (
                      <MainMenuBadge isCoach={item.isForCoach}>
                        {item.badge}
                      </MainMenuBadge>
                    ) : undefined}
                  </MainMenuItem>
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}
    </Observer>
  );
};

export default memo(MainMenu);
